import { useState, useRef } from 'react'
import { useParams } from 'react-router-dom'
import {
  ClipboardDocumentIcon,
  EnvelopeIcon,
  ShareIcon,
  DocumentArrowDownIcon,
  PhotoIcon,
} from '@heroicons/react/24/outline'
import {
  generateBlankPDF,
  generateFilledPDF,
  generateBlankPNG,
  generateFilledJPG,
  generateFilledPNG
} from '../../utils/shareUtils'
import Modal from '../Shared/Modal'
import Spinner from '../Shared/Spinner'
import toast from 'react-hot-toast'
import { setPermission } from '../../services/collabService'
import BlankRecipeExportWrapper from '../Canvas/BlankRecipeExportWrapper'

export default function ShareButton({ recipeCardRef }) {
  const { id: recipeId } = useParams()
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(null) // 'blank-pdf', 'filled-png', etc.
  const [email, setEmail] = useState('')
  const [inviteLoading, setInviteLoading] = useState(false)
  const blankCardRef = useRef();

  const shareUrl = `${window.location.origin}/recipes/${recipeId}/print`
  if (!recipeId) return null

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl)
      toast.success('Link copied to clipboard!')
    } catch {
      toast.error('Failed to copy the link.')
    }
  }

  // Helper wrapper to show loading + feedback
  const runExport = async (fn, label, toastSuccessText) => {
    setLoading(label)
    try {
      const result = await fn()
      toast.success(result?.delivery === 'share' ? 'Export ready to save or share.' : toastSuccessText)
    } catch (err) {
      console.error(err)
      toast.error(`Failed to export ${label.replace('-', ' ')}.`)
    } finally {
      setLoading(null)
    }
  }

  const handleInvite = async () => {
    if (!email) return toast.error("Enter an email address.");
    setInviteLoading(true);

    try {
      const baseUrl = process.env.REACT_APP_RUOTHSTORE_API.replace(/\/$/, '');
      const res = await fetch(
        `${baseUrl}/accounts/verify-user-email/?email=${encodeURIComponent(email)}`,
        { headers: { Accept: 'application/json' } }
      );

      if (!res.ok) {
        const errorText = await res.text();
        console.error("❌ Server returned an error:", errorText);
        toast.error("That email is not registered with Ruoth.");
        return;
      }

      const result = await res.json();

      if (!result.exists) {
        toast.error("That email is not registered with Ruoth.");
        return;
      }

      // ✅ Send full payload including email and optional username
      await setPermission({
        recipe_id: recipeId,
        user_id: result.user_id,
        email: result.email,
        username: result.username, // optional but nice to have
        can_edit: true,
        can_comment: true,
      });

      toast.success(`Shared with ${email}`);
      setEmail('');
    } catch (err) {
      console.error("❌ Invite failed:", err);
      toast.error("Failed to share access. Try again.");
    } finally {
      setInviteLoading(false);
    }
  };

  return (
    <>
      {/* 🔒 Hidden export-only blank layout */}
      <div style={{
        position: 'absolute',
        top: '-9999px',
        left: '-9999px',
        opacity: 0,
        pointerEvents: 'none'
      }}>
        <BlankRecipeExportWrapper recipeCardRef={blankCardRef} shouldBlank={true} />
      </div>
      {/* Trigger Button */}
      <button
        onClick={() => setOpen(true)}
        className="p-2 rounded hover:bg-soft-yellow/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400"
        aria-label="Share recipe"
        title="Share recipe link or export"
      >
        <ShareIcon className="h-6 w-6" />
      </button>

      {/* Modal Content */}
      <Modal open={open} onClose={() => setOpen(false)} title="Share this recipe">
        <p className="text-sm mb-2 text-deep-blue">
          This preview link is perfect for client review or internal kitchen use.
        </p>
        <p className="text-xs text-slate-500 mb-4">
          Anyone with this link can <strong>view</strong> the recipe. Use <span className="underline">Permissions</span> to restrict editing access.
        </p>

        {/* Copy Row */}
        <div className="flex items-center bg-very-light-gray rounded px-3 py-2 mb-4">
          <span className="truncate text-xs grow">{shareUrl}</span>
          <button
            onClick={copy}
            className="ml-3 p-1 rounded hover:bg-soft-yellow/60 focus:outline-none focus-visible:ring"
            aria-label="Copy share URL"
            title="Copy link to clipboard"
          >
            <ClipboardDocumentIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Export Actions – Upgraded for Brand UX */}
        <div className="flex flex-col gap-3 mb-6">

          {/* Filled PDF */}
          <button
            onClick={() => runExport(() => generateFilledPDF(recipeCardRef), 'filled-pdf', 'Filled PDF downloaded!')}
            className="w-full flex flex-col items-center justify-center gap-1.5 bg-dominant-purple text-white rounded-lg py-2 px-4 hover:bg-rich-gold transition disabled:opacity-60"
            disabled={loading}
            title="Download filled recipe as PDF"
          >
            <div className="flex items-center gap-2">
              {loading === 'filled-pdf'
                ? <Spinner size="sm" />
                : <DocumentArrowDownIcon className="w-5 h-5" />}
              <span className="font-medium">Download Filled Recipe (PDF)</span>
            </div>
            <span className="text-xs text-white/80">Perfect for print or client approval</span>
          </button>

          {/* Filled PNG */}
          <button
            onClick={() => runExport(() => generateFilledPNG(recipeCardRef), 'filled-png', 'Filled PNG downloaded!')}
            className="w-full flex flex-col items-center justify-center gap-1.5 bg-dominant-purple text-white rounded-lg py-2 px-4 hover:bg-rich-gold transition disabled:opacity-60"
            disabled={loading}
            title="Download filled recipe as image"
          >
            <div className="flex items-center gap-2">
              {loading === 'filled-png'
                ? <Spinner size="sm" />
                : <PhotoIcon className="w-5 h-5" />}
              <span className="font-medium">Download Filled Recipe (Image)</span>
            </div>
            <span className="text-xs text-white/80">High-res for web or client preview</span>
          </button>

          {/* Filled JPG */}
          <button
            onClick={() => runExport(() => generateFilledJPG(recipeCardRef), 'filled-jpg', 'Filled JPG downloaded!')}
            className="w-full flex flex-col items-center justify-center gap-1.5 bg-dominant-purple text-white rounded-lg py-2 px-4 hover:bg-rich-gold transition disabled:opacity-60"
            disabled={loading}
            title="Download filled recipe as JPG"
          >
            <div className="flex items-center gap-2">
              {loading === 'filled-jpg'
                ? <Spinner size="sm" />
                : <PhotoIcon className="w-5 h-5" />}
              <span className="font-medium">Download Filled Recipe (JPG)</span>
            </div>
            <span className="text-xs text-white/80">High-resolution photo for sharing</span>
          </button>

          {/* Blank PDF */}
          <button
            onClick={() => runExport(() => generateBlankPDF(blankCardRef), 'blank-pdf', 'Blank PDF downloaded!')}
            className="w-full flex flex-col items-center justify-center gap-1.5 bg-white border border-light-gray rounded-lg py-2 px-4 hover:bg-soft-yellow/40 transition disabled:opacity-60"
            disabled={loading}
            title="Download blank recipe card"
          >
            <div className="flex items-center gap-2 text-deep-blue">
              {loading === 'blank-pdf'
                ? <Spinner size="sm" />
                : <DocumentArrowDownIcon className="w-5 h-5" />}
              <span className="font-medium">Download Blank Template (PDF)</span>
            </div>
            <span className="text-xs text-slate-gray">For handwritten use or offline editing</span>
          </button>

          {/* Blank PNG */}
          <button
            onClick={() => runExport(() => generateBlankPNG(blankCardRef), 'blank-png', 'Blank PNG downloaded!')}
            className="w-full flex flex-col items-center justify-center gap-1.5 bg-white border border-light-gray rounded-lg py-2 px-4 hover:bg-soft-yellow/40 transition disabled:opacity-60"
            disabled={loading}
            title="Download blank image card"
          >
            <div className="flex items-center gap-2 text-deep-blue">
              {loading === 'blank-png'
                ? <Spinner size="sm" />
                : <PhotoIcon className="w-5 h-5" />}
              <span className="font-medium">Download Blank Template (Image)</span>
            </div>
            <span className="text-xs text-slate-gray">Use in journals, apps, or prints</span>
          </button>
        </div>

        {/* Email Button */}
        <button
          onClick={() =>
            window.location.href = `mailto:?subject=Ruoth recipe&body=${encodeURIComponent(shareUrl)}`
          }
          className="w-full flex items-center justify-center gap-2 bg-dominant-purple text-white py-2 rounded-lg hover:bg-rich-gold transition-colors"
          title="Send recipe link by email"
        >
          <EnvelopeIcon className="h-5 w-5" />
          <span className="font-medium">Send via Email</span>
        </button>

        {/* 🧑‍🍳 Collaborator Access */}
          <div className="border-t pt-4">
            <h3 className="text-sm font-semibold text-deep-blue mb-2">Share with Collaborator</h3>
            <div className="flex gap-2">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter collaborator’s email"
                className="flex-1 px-3 py-2 border rounded"
              />
              <button
                onClick={handleInvite}
                className="bg-rich-gold text-white px-4 py-2 rounded hover:bg-amber-500"
                disabled={inviteLoading}
              >
                {inviteLoading ? 'Sharing...' : 'Share'}
              </button>
            </div>
          </div>
      </Modal>
    </>
  )
}
